import mustache from 'mustache';
import { v4 as uuid } from 'uuid';
import { Dao } from '../../dao';
import {
  IError,
  IGizmo,
  IGizmoCallbacks,
  ISearchResult,
  TGizmoData,
} from '../../types';
import { gnov } from '../../utils';
import Gizmo from '../gizmo';

interface ISelectGizmoSearchResult {
  searchResult: ISearchResult;
  context: {
    /**
     * Reference to the Gizmo's class instance
     */
    gizmo: Gizmo;
    /**
     * Gizmo callbacks used
     */
    callbacks: IGizmoCallbacks;
    /**
     * Synchronizer's instance
     */
    // synchronizer?: Synchronizer;
    dao?: Dao;
    /**
     * Search results cache
     */
    searchResultsWithData: {
      searchResult: ISearchResult;
      // data: IFilledFormRowItem;
      data: any;
    }[];
  };
}

function selectGizmoSearchResult({
  searchResult,
  context,
}: ISelectGizmoSearchResult): Promise<void> {
  return new Promise<void>(async resolve => {
    const { gizmo } = context;
    const config: IGizmo = gizmo.getConfig();

    // We look for the selected data
    // const sd = searchResultsWithData.find(
    //   s => s.searchResult.id === searchResult.id
    // );

    if (config.ops?.search?.searchResultStatus) {
      const srs = config.ops.search.searchResultStatus.find(
        s => s.status !== undefined && s.status === searchResult.status
      );
      const fluxSrs = config.ops.search.searchResultStatus.filter(
        s => s.flux !== undefined
      );

      // When a search result status is found we set the gizmo's extra validation errors
      if (srs) {
        const message = interpolateError({
          searchResult,
          errorMessage: srs.errorMessage,
        });

        const error: IError = {
          fid: uuid(),
          code: 'search.status',
          message,
        };
        gizmo.setExtraErrors([error]);

        return resolve();
      } else if (fluxSrs.length === 1) {
        const variableMatches = fluxSrs[0].flux?.match(/{{\s*[\w\.]+\s*}}/g);
        let fluxExpression = fluxSrs[0].flux;
        const errorMessage = fluxSrs[0].errorMessage;

        if (variableMatches && fluxExpression) {
          for (const match of variableMatches) {
            if (match.includes('search_result.')) {
              const fluxVar = match
                .replace(/ /g, '')
                .replace('{{', '')
                .replace('}}', '')
                .replace('search_result.', '')
                .trim();

              if (searchResult.answers) {
                const value = gnov(searchResult.answers, `${fluxVar}.0.value`);
                if (value) {
                  fluxExpression = fluxExpression.replace(
                    /{{search_result.q\d+}}/g,
                    `"${value}"`
                  );
                }
              }
            }
          }
        }

        if (fluxExpression) {
          const result = await gizmo
            .getFlux()
            ?.evaluate({ script: fluxExpression });

          if (result === true) {
            const message = interpolateError({
              searchResult,
              errorMessage,
            });

            const error: IError = {
              fid: uuid(),
              code: 'search.status',
              message,
            };
            gizmo.setExtraErrors([error]);

            return resolve();
          }
        }
      }
    }
    gizmo.setExtraErrors([]);

    // We're going to update the gizmo's data with the filled form id, it's used to associate
    // with the origin of the search results
    const gizmoData: TGizmoData = gizmo.getData();

    if (gizmoData && !Array.isArray(gizmoData)) {
      gizmoData.sd = {
        filledFormId: searchResult.id,
      };

      if (searchResult.f_id !== undefined) {
        gizmoData.sd.f_id = searchResult.f_id;
      }
      if (searchResult.formId !== undefined) {
        gizmoData.sd.formId = searchResult.formId;
      }
      if (searchResult.version !== undefined) {
        gizmoData.sd.answers = searchResult.answers;
      }

      await gizmo.setData(gizmoData);
    }

    resolve();
  });
}

interface IInterpolateError {
  searchResult: ISearchResult;
  errorMessage: string;
}

/**
 * Interpolates the error message with the search result answers
 *
 * @param {ISearchResult} searchResult search result being selected by the user
 * @param {string} errorMessage error message to interpolate
 *
 * @returns {string} interpolated error message
 */
function interpolateError({ searchResult, errorMessage }: IInterpolateError) {
  errorMessage = errorMessage.replace(/search_result./g, '');
  const variableMatches = errorMessage.match(/{{\s*[\w\.]+\s*}}/g);
  const mustacheData: any = {};

  if (variableMatches) {
    for (const match of variableMatches) {
      const fluxVar = match
        .replace(/ /g, '')
        .replace('{{', '')
        .replace('}}', '')
        .trim();

      if (searchResult.answers) {
        const value = gnov(searchResult.answers, `${fluxVar}.0.value`);
        if (value !== undefined) {
          mustacheData[fluxVar] = value;
        }
      }
    }
  }

  const message = mustache.render(
    errorMessage.replace(/{{/g, '{{{').replace(/}}/g, '}}}'),
    mustacheData
  );

  return message;
}

export default selectGizmoSearchResult;
